import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

import { E_ShareholderStatuses } from './enums'
import type {
  T_CertificateNominalsList,
  T_UserData,
} from './types'
import type {
  T_CertificateCategoriesListRes,
} from './types/responses'


interface T_AppStore {
  isAuthCheckedOnLoad: boolean
  setToAuthCheckedOnLoad: () => void

  isAuthorized: boolean
  setToAuthorized: () => void
  setToUnauthorized: () => void

  userData: T_UserData
  setUserData: (userData: Partial<T_UserData>) => void
  setMainAccountBalance: (count: number) => void
  setSharesAccountBalance: (count: number) => void

  // Revision Guard для баланса
  balanceRevision: number
  setMainAccountBalanceFromNotification: (count: number) => void
  setMainAccountBalanceFromApi: (count: number, revisionAtRequestStart: number) => void

  certificateCategoriesList: T_CertificateCategoriesListRes
  setCertificateCategoriesList: (certificateCategoriesList: T_CertificateCategoriesListRes) => void
  certificateNominalsList: T_CertificateNominalsList[]
  setCertificateNominalsList: (certificateNominalsList: T_CertificateNominalsList[]) => void

  isPullToRefreshDisabled: boolean
  setPullToRefreshDisabled: (disabled: boolean) => void

  hasStructureMembers: boolean | null
  setHasStructureMembers: (hasMembers: boolean) => void
}

export const useAppStore = create<T_AppStore>()(
  devtools(
    (set) => ({

      //
      // Authorization
      //

      // Флаг, говорящий о том, что первоначальная проверка авторизованности была
      // пройдена. После этого мы точно можем заключить, авторизован юзер или нет.
      // До этого момента мы показываем предлоадер.

      isAuthCheckedOnLoad: false,

      setToAuthCheckedOnLoad: () => set(
        () => ({ isAuthCheckedOnLoad: true }),
        false,
        'setToAuthCheckedOnLoad',
      ),

      // Authorization status

      isAuthorized: false,

      setToAuthorized: () => set(
        () => ({ isAuthorized: true }),
        false,
        'setToAuthorized',
      ),

      setToUnauthorized: () => set(
        () => ({ isAuthorized: false }),
        false,
        'setToUnauthorized',
      ),


      //
      // User profile data
      //

      userData: {
        id: null,
        login: null,
        fullname: '',
        lastname: '',
        firstname: '',
        patronymic: '',
        mobile: null,
        email: null,
        mainAccount: 0,
        sharesAccount: 0,
        shareholderStatusId: E_ShareholderStatuses.None,
        shareholderStatus: '',
        sourceCompanyId: 0,
      },

      setUserData: (newUserData) => set(
        (s) => ({
          userData: {
            ...s.userData,
            ...(newUserData as T_UserData),
          },
        }),
        false,
        'setUserData',
      ),

      setMainAccountBalance: (count: number) => set(
        (s) => ({
          userData: {
            ...s.userData,
            mainAccount: count,
          },
        }),
        false,
        'setMainAccountBalance',
      ),

      //
      // Revision Guard Logic
      //
      balanceRevision: 0,

      setMainAccountBalanceFromNotification: (count: number) => set(
        (s) => ({
          userData: {
            ...s.userData,
            mainAccount: count,
          },
          // Увеличиваем ревизию => все летящие сейчас API запросы станут "устаревшими"
          balanceRevision: s.balanceRevision + 1,
        }),
        false,
        'setMainAccountBalanceFromNotification',
      ),

      setMainAccountBalanceFromApi: (count: number, revisionAtRequestStart: number) => set(
        (s) => {
          // Если ревизия изменилась с момента старта запроса — значит пришла нотификация.
          // Игнорируем этот ответ API, чтобы не откатить данные назад.
          if (s.balanceRevision !== revisionAtRequestStart) {
            // eslint-disable-next-line no-console
            console.warn('[Balance] Race condition protected: API result ignored', {
              apiValue: count,
              storeValue: s.userData.mainAccount,
              revStart: revisionAtRequestStart,
              revCurrent: s.balanceRevision
            })
            return {}
          }

          return {
            userData: {
              ...s.userData,
              mainAccount: count,
            },
          }
        },
        false,
        'setMainAccountBalanceFromApi',
      ),

      setSharesAccountBalance: (count: number) => set(
        (s) => ({
          userData: {
            ...s.userData,
            sharesAccount: count,
          },
        }),
        false,
        'setSharesAccountBalance',
      ),


      //
      // Certificates
      //

      certificateCategoriesList: [],

      setCertificateCategoriesList: (certificateCategoriesList: T_CertificateCategoriesListRes) => set(
        () => ({ certificateCategoriesList }),
        false,
        'setCertificateCategoriesList',
      ),

      certificateNominalsList: [],

      setCertificateNominalsList: (certificateNominalsList: T_CertificateNominalsList[]) => set(
        () => ({ certificateNominalsList }),
        false,
        'setCertificateNominalsList',
      ),

      isPullToRefreshDisabled: false,

      setPullToRefreshDisabled: (disabled: boolean) => set(
        () => ({ isPullToRefreshDisabled: disabled }),
        false,
        'setPullToRefreshDisabled',
      ),

      hasStructureMembers: null,

      setHasStructureMembers: (hasMembers: boolean) => set(
        () => ({ hasStructureMembers: hasMembers }),
        false,
        'setHasStructureMembers',
      ),
    }),

    { store: 'App' },
  )
)
